import type { RectMm } from '../../layout/doorLayout';

/**
 * Reveal inset from each edge of `slabOpeningBounds`. Millimetres.
 * Local offset only — not derived from overall door size or profile artwork.
 */
export const EXTERNAL_SLAB_REVEAL_INSET_MM = 8;
export const EXTERNAL_SLAB_REVEAL_STROKE_MM = 1;

export function isRectWithin(inner: RectMm, outer: RectMm, epsilon = 1e-6): boolean {
  return (
    inner.x + epsilon >= outer.x &&
    inner.y + epsilon >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width + epsilon &&
    inner.y + inner.height <= outer.y + outer.height + epsilon
  );
}

/** Local slab origin: (0, 0) is the top-left of `slabOpeningBounds`. */
export function localSlabBounds(opening: RectMm): RectMm {
  return { x: 0, y: 0, width: opening.width, height: opening.height };
}

/**
 * Reveal rectangle in local slab millimetres.
 * Positioned with a uniform inset from each edge of `slabOpeningBounds`.
 * Returns null when the opening is too small for a visible inset.
 */
export function computeExternalSlabRevealBounds(opening: RectMm): RectMm | null {
  const local = localSlabBounds(opening);
  const halfStroke = EXTERNAL_SLAB_REVEAL_STROKE_MM / 2;
  const inset = Math.min(
    EXTERNAL_SLAB_REVEAL_INSET_MM,
    local.width / 2 - halfStroke,
    local.height / 2 - halfStroke
  );
  if (!(inset > halfStroke)) return null;
  return {
    x: inset,
    y: inset,
    width: local.width - inset * 2,
    height: local.height - inset * 2,
  };
}

export function revealStrokeBounds(reveal: RectMm): RectMm {
  const halfStroke = EXTERNAL_SLAB_REVEAL_STROKE_MM / 2;
  return {
    x: reveal.x - halfStroke,
    y: reveal.y - halfStroke,
    width: reveal.width + EXTERNAL_SLAB_REVEAL_STROKE_MM,
    height: reveal.height + EXTERNAL_SLAB_REVEAL_STROKE_MM,
  };
}
